import styled from "styled-components"

import { UserOrderStatusBadge } from "src/components/Account/BillingPortal/UserOrders/UserOrderStatusBadge"
import { TFetchUserOrdersResponse } from "src/data/billing/types/billingTypes"
import { usePostFetchInvoicePdf } from "src/data/invoices/queries/InvoiceQueries"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useTranslate } from "src/i18n/useTranslate"
import { AccountRoutes } from "src/router/accountRoutes"
import { useRouter } from "src/router/useRouter"
import { MButton } from "src/ui/Button/MButton"
import { GridTable } from "src/ui/GridTable/GridTable"
import {
  TableColumn,
  useTableColumns,
} from "src/ui/GridTable/useTableColumns/useTableColumns"
import { ExternalLink } from "src/ui/Link/ExternalLink"
import { InternalLink } from "src/ui/Link/InternalLink"
import { MText } from "src/ui/MText"
import { downloadFromUrl } from "src/utils/downloadUtil"
import { formatDate } from "src/utils/l10n"

export function UserOrdersTable({
  orders,
  loading,
}: {
  orders: TFetchUserOrdersResponse["orders"]
  loading: boolean
}) {
  const { t, langKeys } = useTranslate()
  const { navigate } = useRouter()
  const user = useGetUser()
  const postFetchInvoicePdf = usePostFetchInvoicePdf()

  function handleDownloadInvoiceClick(invoiceId: string) {
    postFetchInvoicePdf.mutate(
      { invoiceId },
      {
        onSuccess: (url) => {
          downloadFromUrl(url)
        },
      }
    )
  }

  const tableColumns: TableColumn<
    TFetchUserOrdersResponse["orders"][number]
  >[] = [
    {
      value: "order_number",
      label: t(langKeys.order_number),
      renderCell: (order) => (
        <InternalLink
          to={
            AccountRoutes.AccountOrderDetails.location(order.order_number)
              .pathname
          }
        >
          #{order.order_number}
        </InternalLink>
      ),
      columnWidth: "auto",
    },
    {
      value: "date",
      label: t(langKeys.date),
      renderCell: (order) => (
        <MText variant="bodyS">
          {formatDate({
            date: order.created_at,
            clockType: user.clock_type,
            excludeTime: true,
          })}
        </MText>
      ),
      columnWidth: "auto",
    },
    {
      value: "status",
      label: t(langKeys.status),
      renderCell: (order) => (
        <FlexStart>
          <UserOrderStatusBadge status={order.status} />
        </FlexStart>
      ),
      columnWidth: "auto",
    },
    {
      value: "tracking",
      label: t(langKeys.orders_tracking),
      renderCell: (order) => (
        <div>
          <MText variant="bodyS">
            {order.tracking_url ? (
              <ExternalLink href={order.tracking_url}>
                {t(langKeys.orders_view_tracking)}
              </ExternalLink>
            ) : (
              t(langKeys.orders_tracking_not_available)
            )}
          </MText>
        </div>
      ),
      columnWidth: "auto",
    },
    {
      value: "invoice",
      label: t(langKeys.invoice),
      renderCell: (order) => (
        <div>
          {order.invoice_id && (
            <div>
              <MButton
                variant="minimal"
                size="small"
                loading={postFetchInvoicePdf.isLoading}
                onClick={() =>
                  handleDownloadInvoiceClick(order.invoice_id || "")
                }
              >
                {t(langKeys.download)}
              </MButton>
            </div>
          )}
        </div>
      ),
      columnWidth: "auto",
    },
  ]

  const { headerElements, rows, templateColumns } = useTableColumns({
    columns: tableColumns,
    data: orders,
  })

  return (
    <GridTable
      header={headerElements}
      loading={loading}
      rows={rows || []}
      mobileRows={rows || []}
      minWidth={0}
      templateColumns={templateColumns}
      horizontalScroll
      onRowClick={(index) => {
        navigate(
          AccountRoutes.AccountOrderDetails.location(
            orders?.[index]?.order_number || ""
          ).pathname
        )
      }}
    />
  )
}

const FlexStart = styled.div`
  display: flex;
  align-items: flex-start;
`
